import {
  faCheck,
  faFile,
  faPen,
  faPlus,
  faSearch,
  faTag,
  faTimes,
  faTrash,
  faUser,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import EmptyList from 'components/common/EmptyList';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import DialogModal from 'components/modals/DialogModal';
import Pagination from 'components/pagination';
import { useFormatDateTime } from 'hooks/useFormatDateTime';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useNotification from 'hooks/useNotification';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  FormControl,
  InputGroup,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import TagsService from 'services/TagsService.service';

const TagsRegistration = () => {
  // Hook para notificações
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const formatDateTime = useFormatDateTime();

  // Estados
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [tags, setTags] = useState({
    currentPage: 0,
    hasNextPage: false,
    totalPages: 0,
    totalElementsPerPage: 20,
    totalElements: 0,
    content: []
  });

  // Estado para controlar edição de tags
  const [editingTagId, setEditingTagId] = useState(null);
  const [editedTagName, setEditedTagName] = useState('');

  // Estado para controlar a adição de nova tag
  const [newTagName, setNewTagName] = useState('');

  // Estados para modal de exclusão
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tagsToDelete, setTagsToDelete] = useState([]);
  const [filterNameTag, setFilterNameTag] = useState('');

  // Filtros para paginação
  const [filters, setFilters] = useState({
    page: 0,
    size: 20,
    name: ''
  });

  const getTags = async () => {
    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams(filters).toString();

      const response = await TagsService.list(queryParams.toString());
      if (response) {
        setTags(response);
      }
    } catch (error) {
      console.error('Erro ao buscar tags:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = e => {
    const searchValue = e.target.value;
    setFilterNameTag(searchValue);

    if (searchValue === '') {
      setFilters(prevFilters => ({
        ...prevFilters,
        page: 0,
        name: ''
      }));
    }
  };

  const handleSearch = () => {
    setFilters(prevFilters => ({
      ...prevFilters,
      page: 0,
      name: filterNameTag
    }));
  };

  // Função para iniciar edição de tag
  const startEditing = tag => {
    setEditingTagId(tag.id);
    setEditedTagName(tag.name);
  };

  // Função para salvar a tag editada
  const saveTagEdit = async () => {
    if (editedTagName.trim()) {
      try {
        const tagData = { name: editedTagName };
        const updatedTag = await TagsService.edit(tagData, editingTagId);

        // Update the tag in the local state
        const updatedContent = tags.content.map(tag =>
          tag.id === editingTagId ? { ...tag, name: editedTagName } : tag
        );

        setTags({
          ...tags,
          content: updatedContent
        });

        showSuccessNotification('Tag atualizada com sucesso!');
      } catch (error) {
        console.error('Erro ao atualizar tag:', error);
        // Mostrar notificação de erro
        if (
          error.response &&
          error.response.data &&
          error.response.data.userMessage
        ) {
          showErrorNotification(error.response.data.userMessage);
        } else {
          showErrorNotification(
            'Erro ao atualizar tag. Verifique se já existe uma tag com este nome.'
          );
        }
      } finally {
        setEditingTagId(null);
      }
    }
  };

  // Função para adicionar nova tag
  const addNewTag = async () => {
    if (newTagName.trim()) {
      const tagData = { name: newTagName };
      const response = await TagsService.create(tagData);

      if (!response.userMessage) {
        setTags({
          ...tags,
          content: [...tags.content, response],
          totalElements: tags.totalElements + 1
        });
        showSuccessNotification('Tag criada com sucesso!');
        setNewTagName('');
      } else {
        showErrorNotification(`Erro: ${response.userMessage}`);
      }
    }
  };

  // Função para abrir modal de exclusão para uma única tag
  const openDeleteModal = tagId => {
    setTagsToDelete([tagId]);
    setShowDeleteModal(true);
  };

  // Função para abrir modal de exclusão para múltiplas tags
  const openBulkDeleteModal = () => {
    const selectedTagIds = table
      .getSelectedRowModel()
      .flatRows.map(row => row.original.id);
    if (selectedTagIds.length > 0) {
      setTagsToDelete(selectedTagIds);
      setShowDeleteModal(true);
    }
  };

  const confirmDelete = async () => {
    try {
      if (tagsToDelete.length === 1) {
        // Delete single tag
        await TagsService.delete(tagsToDelete[0]);
      } else if (tagsToDelete.length > 1) {
        // Delete multiple tags
        await TagsService.batchDelete({ tagIds: tagsToDelete });
      }

      // Update local state by removing deleted tags
      const updatedContent = tags.content.filter(
        tag => !tagsToDelete.includes(tag.id)
      );

      setTags({
        ...tags,
        content: updatedContent,
        totalElements: tags.totalElements - tagsToDelete.length
      });

      // Reset selection
      table.resetRowSelection();

      // Notificar sucesso
      showSuccessNotification(
        tagsToDelete.length === 1
          ? 'Tag excluída com sucesso!'
          : `${tagsToDelete.length} tags excluídas com sucesso!`
      );
    } catch (error) {
      console.error('Erro ao excluir tags:', error);

      // Mostrar notificação de erro
      if (
        error.response &&
        error.response.data &&
        error.response.data.userMessage
      ) {
        showErrorNotification(error.response.data.userMessage);
      } else {
        showErrorNotification(
          'Erro ao excluir tag(s). Tente novamente mais tarde.'
        );
      }
    } finally {
      setShowDeleteModal(false);
    }
  };

  // Função para formatar data
  const formatDate = dateString => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Efeito para buscar as tags quando os filtros mudam
  useEffect(() => {
    getTags();
  }, [filters]);

  // Define columns for AdvanceTable
  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Nome',
        cell: ({ row: { original } }) =>
          editingTagId === original.id ? (
            <InputGroup size="sm">
              <FormControl
                value={editedTagName}
                onChange={e => setEditedTagName(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && saveTagEdit()}
                autoFocus
              />
              <Button
                data-testid="tag-edit-confirm"
                variant="falcon-success"
                size="sm"
                onClick={saveTagEdit}
              >
                <FontAwesomeIcon icon={faCheck} />
              </Button>
              <Button
                variant="falcon-danger"
                size="sm"
                onClick={() => setEditingTagId(null)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </InputGroup>
          ) : (
            <div
              className="d-flex align-items-center"
              onClick={() => startEditing(original)}
              style={{ cursor: 'pointer' }}
            >
              <FontAwesomeIcon icon={faTag} className="me-2 text-primary" />
              {original.name}
            </div>
          ),
        meta: {
          headerProps: { className: 'text-900' },
          cellProps: { className: 'py-2' }
        }
      },
      {
        accessorKey: 'customersCount',
        header: '',
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <OverlayTrigger overlay={<Tooltip>Clientes</Tooltip>}>
            <Button
              variant="link"
              className="text-center text-900 text-decoration-none"
              disabled={original.customersCount === 0}
            >
              <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
              {original.customersCount}
            </Button>
          </OverlayTrigger>
        )
      },
      {
        accessorKey: 'contactCustomersCount',
        header: '',
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <OverlayTrigger overlay={<Tooltip>Contatos</Tooltip>}>
            <Button
              variant="link"
              className="text-center text-900 text-decoration-none"
              disabled={original.contactCustomersCount === 0}
            >
              <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
              {original.contactCustomersCount}
            </Button>
          </OverlayTrigger>
        )
      },
      {
        accessorKey: 'kbArticlesCount',
        header: '',
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <OverlayTrigger overlay={<Tooltip>Artigos</Tooltip>}>
            <Button
              variant="link"
              className="text-center text-900 text-decoration-none"
              disabled={original.kbArticlesCount === 0}
            >
              <FontAwesomeIcon icon={faFile} className="me-2 text-primary" />
              {original.kbArticlesCount}
            </Button>
          </OverlayTrigger>
        )
      },
      {
        accessorKey: 'creationDate',
        header: 'Data Criação',
        cell: ({ row: { original } }) => (
          <div className="text-center">
            {formatDateTime(original.creationDate)}
          </div>
        ),
        meta: {
          headerProps: { className: 'text-center text-900' },
          cellProps: { className: 'text-center' }
        }
      },
      {
        accessorKey: 'lastUpdateDate',
        header: 'Data Atualização',
        cell: ({ row: { original } }) => (
          <div className="text-center">
            {formatDateTime(original.lastUpdateDate)}
          </div>
        ),
        meta: {
          headerProps: { className: 'text-center text-900' },
          cellProps: { className: 'text-center' }
        }
      },
      {
        accessorKey: 'actions',
        header: 'Ações',
        cell: ({ row: { original } }) => (
          <div className="text-center">
            <Button
              variant="link"
              size="sm"
              className="me-3 p-0"
              onClick={() => startEditing(original)}
            >
              <FontAwesomeIcon icon={faPen} />
            </Button>
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={() => openDeleteModal(original.id)}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </div>
        ),
        meta: {
          headerProps: { className: 'text-center text-900' },
          cellProps: { className: 'text-center' }
        }
      }
    ],
    [editingTagId, editedTagName]
  );

  // Use the hook with the data
  const table = useAdvanceTable({
    data: tags.content || [],
    columns,
    sortable: true,
    selection: true
  });

  const isEmpty = !isLoading && tags.content?.length === 0;
  const selectedRowCount = table.getSelectedRowModel().rows.length;

  return (
    <>
      <Row>
        <Col md={9} className="pe-0">
          <FalconComponentCard>
            <FalconComponentCard.Header title="Gerenciar tags" />
            <FalconComponentCard.Body>
              <Flex justifyContent="between" className="mb-3">
                {' '}
                {/* Aumentei o gap para 4 (1.5rem) */}
                {/* Campo de busca */}
                <div className="flex-grow-1 me-11">
                  {' '}
                  {/* Ocupa o espaço disponível */}
                  <InputGroup>
                    <FormControl
                      size="sm"
                      id="search"
                      type="search"
                      className="shadow-none"
                      placeholder="Buscar por nome"
                      value={filterNameTag}
                      onChange={handleSearchChange}
                    />
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="border-300 hover-border-secondary"
                      onClick={handleSearch}
                    >
                      <FontAwesomeIcon icon={faSearch} className="fs-10" />
                    </Button>
                  </InputGroup>
                </div>
                {/* Campo para adicionar nova tag */}
                <div className="flex-grow-1 ms-9">
                  {' '}
                  {/* Ocupa o espaço disponível */}
                  <InputGroup>
                    <FormControl
                      size="sm"
                      placeholder="Adicionar tag"
                      value={newTagName}
                      onChange={e => setNewTagName(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && addNewTag()}
                    />
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="border-300 hover-border-secondary"
                      onClick={addNewTag}
                    >
                      <FontAwesomeIcon
                        icon={faPlus}
                        size="sm"
                        className="me-1"
                      />
                    </Button>
                  </InputGroup>
                </div>
              </Flex>

              <div>
                {isLoading ? (
                  <Skeleton count={5} height={44} baseColor="#5E6E82" />
                ) : (
                  <div className="d-flex flex-column">
                    <div className="align-items-center">
                      <Flex justifyContent={'between'} className={'mb-2'}>
                        <div></div>
                        {selectedRowCount > 0 && (
                          <div>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={openBulkDeleteModal}
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                className="me-1"
                              />
                              Excluir selecionados ({selectedRowCount})
                            </Button>
                          </div>
                        )}
                      </Flex>

                      {isEmpty ? (
                        <EmptyList imgSize={400} vhSize={73} />
                      ) : (
                        <>
                          <AdvanceTableProvider {...table}>
                            <AdvanceTable
                              table
                              headerClassName="text-800 align-middle"
                              rowClassName="btn-reveal-trigger align-middle"
                              tableProps={{
                                size: 'sm',
                                className: 'fs-md mb-0 overflow-hidden'
                              }}
                            />
                          </AdvanceTableProvider>
                          <Card.Footer>
                            <Pagination
                              data={tags}
                              filters={filters}
                              setFilters={setFilters}
                            />
                          </Card.Footer>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Col>

        <Col xl={3}>
          <FalconComponentCard>
            <FalconComponentCard.Body>
              <p className="fw-bold">Gerenciamento de Tags</p>
              <p className="fs-10">
                Nesta seção, você encontrará uma listagem de todas as tags
                utilizadas no seu sistema de helpdesk. Por padrão, é possível
                visualizar rapidamente quantas vezes cada tag foi aplicada em
                artigos, a data de criação e de modificação da tag.
              </p>
              <br />
              <p className="fw-bold">Edição e Mesclagem de Tags</p>
              <p className="fs-10">
                Se desejar alterar o nome de uma tag, basta clicar sobre ela ou
                pode clicar no ícone de caneta para editá-la — a alteração será
                aplicada automaticamente a todos os locais onde ela estiver em
                uso.
              </p>
              <br />
              <p className="fw-bold">Exclusão de Tags</p>
              <p className="fs-10">
                Para excluir uma tag, clique no ícone de lixeira ao lado da tag
                desejada. Você também pode selecionar múltiplas tags usando as
                caixas de seleção e então usar o botão "Excluir selecionados"
                que aparecerá no topo da tabela.
              </p>
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Col>
      </Row>

      {/* Modal de confirmação de exclusão */}
      <DialogModal
        handleClose={() => setShowDeleteModal(false)}
        message={`Deseja excluir ${
          tagsToDelete.length > 1 ? 'estas tags' : 'esta tag'
        }?`}
        showModal={showDeleteModal}
        response={confirmDelete}
      />
    </>
  );
};

export default TagsRegistration;
